import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import z from 'zod';

import { RmvField } from '@/components/management/formElements/RmvField';
import { useAppForm } from '@/hooks/useAppForm';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { useUserSuspenseQuery } from '@/hooks/useUserSuspenseQuery';
import type { CreateParkAndRideFacilityDto, Organization } from '@/modules/api/backend';
import { formScrollIntoViewOptions } from '@/utils/formScrollIntoViewOptions';

type CreateParkingFacilityFormValues = Omit<CreateParkAndRideFacilityDto, 'stationId'>;

interface CreateParkingFacilityFormProps {
  rmvId?: string;
  organizations: Organization[];
  setRef: (idx: number) => (ref: HTMLDivElement | null) => void;
  tabs: string[];
  onSubmit: (values: CreateParkingFacilityFormValues) => Promise<void>;
  disabled?: boolean;
}

export function CreateParkAndRideFacilityForm({
  rmvId,
  organizations,
  setRef,
  tabs,
  onSubmit,
  disabled,
}: CreateParkingFacilityFormProps) {
  const { data: user } = useUserSuspenseQuery();
  const userOrgId = user.organization?.organizationId;

  const form = useAppForm({
    defaultValues: {
      name: '',
      rmvId: rmvId ?? '',
      rmvSuffix: '',
      organizationId: '',
    },
    ...formScrollIntoViewOptions,
    onSubmit: async ({ value }) => {
      const finalRmvId = `${value.rmvId}${value.rmvSuffix.trim()}`;
      const { rmvSuffix, ...rest } = value;
      await onSubmit({
        ...rest,
        rmvId: finalRmvId,
      });
    },
  });

  useEffect(() => {
    if (disabled && userOrgId) {
      form.setFieldValue('organizationId', userOrgId);
    }
  }, [disabled, form, userOrgId]);

  useNavigationBlocker(form);

  return (
    <form
      id="parkingFacilityForm"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Stack spacing={6}>
        <Stack spacing={2}>
          <Typography ref={setRef(0)} variant="h2">
            {tabs[0]}
          </Typography>

          <form.AppField
            name="name"
            validators={{
              onChange: z.string().nonempty(),
            }}
          >
            {(field) => <field.TextField label="Name" sx={{ marginBottom: 2 }} />}
          </form.AppField>

          {rmvId ? (
            <RmvField fields={{ suffix: 'rmvSuffix' }} form={form} prefix={rmvId ?? ''} validate={!!rmvId} />
          ) : null}

          <form.AppField
            name="organizationId"
            validators={{
              onChange: z.string().nonempty(),
            }}
          >
            {(field) => (
              <field.SelectField
                disabled={disabled}
                label="Organisation"
                sx={{ mb: 1 }}
                options={organizations.map((org) => ({
                  value: org.id,
                  label: org.name,
                }))}
              />
            )}
          </form.AppField>
        </Stack>
      </Stack>
    </form>
  );
}
